/**
 * NEW NestJS payments-runtime wallet endpoints (ledger-backed, per organization+currency). Base URL already
 * includes `/api/v1` via HttpApiService. See `WALLETS-handoff-frontend.md` for the full contract.
 */
export const PAYMENTS_RUNTIME_WALLETS_ENDPOINTS = {
  WALLETS: "/internal/payments/wallets",
  CURRENCIES: "/internal/payments/wallets/currencies",
  SUMMARY: "/internal/payments/wallets/summary",
  PROVIDER_FLOATS: "/internal/payments/provider-balances/overview",
  PROVIDER_FLOATS_REFRESH: "/internal/payments/provider-balances/overview/refresh",
  PAYOUT_RECHECK: "/internal/payments/open-payouts/recheck",
  RECENT_TRANSACTIONS: "/internal/payments/wallets/recent-transactions",
  MOVEMENTS: "/internal/payments/movements",
  ORDER: "/internal/payments/wallets/order",
  byId: (id: string) => `/internal/payments/wallets/${id}`,
  transactions: (id: string) => `/internal/payments/wallets/${id}/transactions`,
  transaction: (id: string, transactionId: string) =>
    `/internal/payments/wallets/${id}/transactions/${transactionId}`,
  freeze: (id: string) => `/internal/payments/wallets/${id}/freeze`,
  unfreeze: (id: string) => `/internal/payments/wallets/${id}/unfreeze`,
} as const;

/** Query param naming the statement movement whose detail is open. */
export const STATEMENT_MOVEMENT_PARAM = "movement";
/** On the wallets home, which card the open movement belongs to. */
export const STATEMENT_WALLET_PARAM = "wallet";
/** On the wallets home, opens the COP wallet's send panel on the camera (sidebar entry, app shortcut). */
export const PAY_QR_PARAM = "pay_qr";

export const CURRENCY_TO_COUNTRY: Record<string, string> = {
  COP: "Colombia",
  BRL: "Brasil",
  MXN: "México",
  ARS: "Argentina",
  CLP: "Chile",
  PEN: "Perú",
  PYG: "Paraguay",
  GTQ: "Guatemala",
  CRC: "Costa Rica",
  EUR: "Europa",
  USD: "Estados Unidos · Ecuador · Panamá",
};

// Enables navigation from the wallets page to the dispersions view with origin pre-selected
export const CURRENCY_TO_DISPERSION_CODE: Record<string, string> = {
  COP: "COL",
  BRL: "BRA",
  MXN: "MEX",
  ARS: "ARG",
  CLP: "CHL",
  PEN: "PER",
  PYG: "PRY",
  GTQ: "GTM",
  USDT: "CRY",
  USDC: "CRY",
  EUR: "EUR",
  // USD is used in Ecuador and Panama, handled by country code instead
};

/**
 * Currencies whose PAYOUTS run on the payments runtime (same-currency dispersions to Colombia/Brazil/Mexico/
 * Argentina). The dispersions view only supports these corridors, so wallets in other currencies have no
 * "send" flow. Kept as a local currency set to avoid a wallets→dispersions circular import; mirrors
 * dispersions' RUNTIME_CORRIDOR_ISO3.
 */
export const RUNTIME_PAYOUT_CURRENCIES = new Set(["COP", "BRL", "MXN", "ARS"]);

/**
 * PROVISIONAL product gate. The only currencies `/app/wallets` offers to activate, and the only ones it lists
 * — every other currency the backend catalog returns is filtered out of both the activation modal and the
 * wallet grid.
 *
 * Why: only these four corridors are automated end to end, collections AND dispersions (the same four as
 * dispersions' `RUNTIME_CORRIDOR_ISO3`). A wallet in any other currency can hold a balance, but it has no
 * self-service top-up and no send flow, so offering it reads as a feature that exists and then dead-ends.
 *
 * **The excluded currencies are kept below COMMENTED OUT rather than deleted, on purpose.** The gate is
 * temporary and lifts one currency at a time as its corridor is automated, so the next change here is
 * uncommenting a line — the list IS the record of what was withheld. That is the only reason there is
 * commented-out code in this file; it is not a leftover.
 *
 * Deliberately NOT derived from `RUNTIME_PAYOUT_CURRENCIES` above, which holds the same four today: that one
 * answers "does this wallet have a send flow?", this one answers "may it appear on screen at all?". The two
 * diverge the moment a corridor gains collections before dispersions, or the reverse.
 *
 * Two things this gate does NOT do, and must not be read as doing:
 *
 * - **It is not enforcement.** `POST /internal/payments/wallets` still accepts every catalog currency, so this
 *   withdraws the affordance and nothing more.
 * - **It does not reach the payout origin selector nor the payout-batch setup**, which read the wallet list
 *   through `useRuntimeWallets` directly. A wallet hidden here stays selectable as a cross-currency origin in
 *   "Enviar dinero", which is a supported path (docs/payments-quote-flow.md) and has to keep working.
 */
export const ACTIVATABLE_WALLET_CURRENCIES = new Set([
  "ARS",
  "BRL",
  "COP",
  "MXN",
  // Withheld while their corridor is not automated — uncomment the line when it ships:
  // "CLP",
  // "PEN",
  // "PYG",
  // "GTQ",
  // "CRC",
  // "EUR",
  // "USD",
]);

/** Whether `/app/wallets` may offer or list this currency at all — see `ACTIVATABLE_WALLET_CURRENCIES`. */
export const isActivatableWalletCurrency = (currency: string): boolean =>
  ACTIVATABLE_WALLET_CURRENCIES.has(currency.trim().toUpperCase());

// Flag code overrides for currencies without a dispersion code or whose code isn't an ISO country code
// (EU supranational; USD is multi-country and CRC has no dispersion corridor yet).
export const CURRENCY_TO_FLAG_CODE: Record<string, string> = {
  EUR: "EU",
  USD: "US",
  CRC: "CR",
};

/**
 * Display names of the payment providers, for two staff surfaces only. Rule 12 keeps provider identity off
 * every screen; the treasury tab (2026-10-15, how much each provider holds) and the payment incidents panel
 * (2026-10-19, the claims staff take to the provider and the text they paste in its channel) are the agreed
 * exceptions, both INTERNAL-only. An unknown code renders as-is.
 */
export const PROVIDER_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  KAMIN: "Kamin",
  BITSO: "Bitso",
  INITIUM: "Initium",
  BEPAY: "Bepay",
};
